"use client";

/**
 * QNAVSEED — carries the SERVER-resolved quick-nav tab list down to
 * `<ProductQuickNav />` so the rail is real content on the FIRST render.
 *
 * ── WHY A CONTEXT AND NOT A PROP ─────────────────────────────────────────────
 * The other eight `/products` seeds (`initialCategoryTree`, `initialColours`,
 * `initialFlowers`, `initialProducts`, `initialTotal`,
 * `initialPriceRangePence`, `initialPage`, `initialIsMobile`) are threaded as
 * props through `ProductsClient`, and that would be the consistent home for a
 * ninth. `ProductQuickNav` is rendered from inside `ProductsClient`, which is
 * owned by concurrent work and must not be edited here, so the seed takes the
 * one route that reaches the rail without touching the intermediate component.
 *
 * It is also the shape `.claude/rules/state-management.md` prescribes for this
 * exact case: server-derived, subtree-scoped state that must be correct on the
 * first render belongs in a Context, because a zustand store is a module
 * singleton whose initial value is fixed at module eval (and on Workers the
 * isolate is shared across requests, so writing one during render leaks between
 * them). A context value is created per render tree, so it is already correct in
 * the SSR HTML.
 *
 * ⚠ If `ProductsClient` ever becomes editable, prefer converting this to an
 * `initialNavTabs` prop for consistency with its eight neighbours.
 */

import { useMemo, type ReactNode } from "react";
import {
  ProductNavTabsSeedContext,
  type PublicNavTab,
} from "@/hooks/useProductNavTabs";

export interface ProductNavTabsSeedProps {
  /** Server-resolved tabs (`listPublicNavTabs`). `[]` is meaningful: it means
   *  the admin has configured none, and the rail correctly renders nothing. */
  tabs: PublicNavTab[];
  /** The collection scope the tabs were resolved for — `null` on `/products`. */
  scope?: string | null;
  children: ReactNode;
}

export function ProductNavTabsSeed({
  tabs,
  scope = null,
  children,
}: ProductNavTabsSeedProps) {
  const value = useMemo(() => ({ scope, tabs }), [scope, tabs]);
  return (
    <ProductNavTabsSeedContext value={value}>
      {children}
    </ProductNavTabsSeedContext>
  );
}
